<script setup lang="ts">
import { OrButtonV3, OrCheckboxV3, OrInputV3, OrNotificationV3 } from '@onereach/ui-components';
import { ref } from 'vue';

import OrSubtitles from '@/components/OrLoginSubtitles/OrSubtitles.vue';
import OrLogo from '@/components/OrLogo/OrLogo.vue';
import useI18n from '@/composables/useI18n';

const { translate } = useI18n();
const props = withDefaults(defineProps<{
  serviceError: string;
  password: string | undefined;
  username: string | undefined;
  readonlyLogin?: boolean;
  keepLoggedIn: boolean;
  isExternalLoading: boolean;
  isLoading: boolean;
  externalProviders?: Record<string, string>[];
}>(), {
  isExternalLoading: false,
  externalProviders: () => [],
});

const emit = defineEmits<{
  (e: 'submit'): void;
  (e: 'initExternalLogin', value: {
    name: string;
    redirectPath: string | null;
  }): void;
  (e: 'showForgotPopup'): void;
  (e: 'update:username', value: string | undefined): void;
  (e: 'update:password', value: string | undefined): void;
  (e: 'update:keepLoggedIn', value: boolean): void;
}>();

const updateEmail = (value: string | undefined) => {
  const trimmed = value === undefined ? value : value.trim();
  emit('update:username', trimmed);
  return trimmed;
};

const emailError = ref('');
const passwordError = ref('');

const submit = () => {
  emailError.value = !props.username ? translate('login.password_restore.This field is required') : '';
  if (emailError.value) return;

  passwordError.value = !props.password ? translate('login.password_restore.This field is required') : '';
  if (passwordError.value) return;

  emit('submit');
};
</script>

<template>
  <div
    class="form layout-column p-md sm:p-0 gap-xl w-full sm:w-[343px] md:w-[352px]"
  >
    <OrLogo
      class="self-center md:hidden mb-md"
    />
    <div class=" layout-column gap-md items-center md:items-stretch">
      <h2 class="typography-display-small dark:text-on-background-dark">
        {{ translate('login.login.signInToOnereachai') }}
      </h2>
      <OrSubtitles />
    </div>
    <div class="layout-column gap-md">
      <OrNotificationV3
        v-if="serviceError"
        class="error"
        variant="error"
        disable-close
      >
        {{ serviceError }}
      </OrNotificationV3>
      <OrInputV3
        :model-value="username"
        data-test="username"
        class="username"
        name="username"
        type="text"
        :readonly="readonlyLogin"
        :error="emailError || !!serviceError"
        :label="translate('login.login.emailAddress')"
        :placeholder="translate('login.login.Enter email')"
        :disabled="isLoading || isExternalLoading"
        @update:model-value="updateEmail"
        @keyup.enter.native="submit"
      />
      <OrInputV3
        :model-value="password"
        class="password"
        data-test="password"
        type="password"
        :error="passwordError || !!serviceError"
        :label="translate('login.login.password')"
        :disabled="isLoading || isExternalLoading"
        :placeholder="translate('login.login.Enter password')"
        @update:model-value="emit('update:password', $event)"
        @keyup.enter.native="submit"
      />
      <div class="layout-row justify-between gap-md">
        <OrCheckboxV3
          :model-value="keepLoggedIn"
          data-test="keepLoggedIn"
          class="form-checkbox grey"
          :disabled="isLoading || isExternalLoading"
          @update:model-value="emit('update:keepLoggedIn', $event)"
          @keyup.enter.native="submit"
        >
          {{ translate('login.login.Keep me signed in') }}
        </OrCheckboxV3>
        <OrButtonV3
          variant="text"
          class="!p-0"
          :disabled="isLoading || isExternalLoading"
          @click.prevent="emit('showForgotPopup')"
        >
          {{ translate('login.login.forgotPassword') }}
        </OrButtonV3>
      </div>
    </div>
    <slot name="captcha" />
    <div class="layout-column gap-md">
      <OrButtonV3
        data-test="submit"
        :loading="isLoading"
        :disabled="isExternalLoading"
        class="sign-in "
        type="submit"
        @click="submit"
      >
        {{ translate('login.login.signIn') }}
      </OrButtonV3>
      <slot name="google-login" />
      <slot name="passkey-login" />

      <slot name="external-providers" />
    </div>
    <slot name="signup" />
    <slot name="skip" />
  </div>
</template>
